import { apiClient } from '@/api/client'
import type { OnlineSessionRow, WebinarEvent, WebinarTimeZoneDisplay } from './types'

const ONLINE_SLOTS_START_DATE = '2026-06-01'
const DEFAULT_WEBINAR_TIME_ZONE = 'America/Chicago'
const USA_TIME_ZONE_PATTERN =
  /^(America\/(?:Adak|Anchorage|Boise|Chicago|Denver|Detroit|Indiana\/(?:Indianapolis|Knox|Marengo|Petersburg|Tell_City|Vevay|Vincennes|Winamac)|Juneau|Kentucky\/(?:Louisville|Monticello)|Los_Angeles|Menominee|Metlakatla|New_York|Nome|North_Dakota\/(?:Beulah|Center|New_Salem)|Phoenix|Sitka|Yakutat)|Pacific\/Honolulu)$/
const WEBINAR_TIMEZONE_DISPLAY_ORDER = ['CDT', 'EDT', 'MDT', 'PDT'] as const

export async function fetchWebinarEvents(isoDate: string): Promise<WebinarEvent[]> {
  type WebinarResponse = { content?: WebinarEvent[] }
  const res = await apiClient.get<WebinarResponse>(
    `/api/v1/cast/recurring/events?from=${encodeURIComponent(`${isoDate}T00:00:00`)}&to=${encodeURIComponent(`${isoDate}T23:59:59`)}&page=0&size=6`
  )
  return res.data?.content ?? []
}

export function formatSlotTime(timeStr: string): string {
  const [hStr, mStr] = timeStr.split(':')
  let hour = parseInt(hStr, 10)
  const minute = parseInt(mStr, 10)
  const period = hour >= 12 ? 'PM' : 'AM'
  if (hour > 12) hour -= 12
  if (hour === 0) hour = 12
  return `${hour}:${String(minute).padStart(2, '0')} ${period}`
}

export function formatMeetingDate(dateString: string): string {
  let normalized = dateString
  if (dateString.includes('/')) {
    const [month, day, year] = dateString.split('/')
    if (month && day && year && year.length === 4) {
      normalized = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
  }
  const [year, month, day] = normalized.split('-').map(Number)
  if (!year || !month || !day) return dateString
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  })
}

export function getOrderedTimeZoneDisplays(event: WebinarEvent): WebinarTimeZoneDisplay[] {
  return [...(event.timeZoneDisplays ?? [])].sort((left, right) => {
    const leftIndex = WEBINAR_TIMEZONE_DISPLAY_ORDER.indexOf(
      left.abbreviation as (typeof WEBINAR_TIMEZONE_DISPLAY_ORDER)[number]
    )
    const rightIndex = WEBINAR_TIMEZONE_DISPLAY_ORDER.indexOf(
      right.abbreviation as (typeof WEBINAR_TIMEZONE_DISPLAY_ORDER)[number]
    )

    const normalizedLeftIndex = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex
    const normalizedRightIndex = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex

    return normalizedLeftIndex - normalizedRightIndex
  })
}

function getPrimaryWebinarDisplay(event: WebinarEvent): WebinarTimeZoneDisplay | null {
  return getOrderedTimeZoneDisplays(event)[0] ?? null
}

export function getWebinarRawTime(event: WebinarEvent): string {
  const primaryDisplay = getPrimaryWebinarDisplay(event)

  if (primaryDisplay) {
    return `${String(primaryDisplay.scheduledStartHour).padStart(2, '0')}:${String(primaryDisplay.scheduledStartMinute).padStart(2, '0')}`
  }

  const scheduledStart = new Date(event.scheduledStartAt)
  return `${String(scheduledStart.getUTCHours()).padStart(2, '0')}:${String(scheduledStart.getUTCMinutes()).padStart(2, '0')}`
}

export function getWebinarSelectionLabel(event: WebinarEvent): string {
  const primaryDisplay = getPrimaryWebinarDisplay(event)

  if (primaryDisplay) {
    return `${primaryDisplay.scheduledStartTime} ${primaryDisplay.abbreviation}`
  }

  return formatSlotTime(getWebinarRawTime(event))
}

export function getEffectiveWebinarTimeZone(): string {
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return browserTimeZone && USA_TIME_ZONE_PATTERN.test(browserTimeZone)
    ? browserTimeZone
    : DEFAULT_WEBINAR_TIME_ZONE
}

function getTimeInZone(value: string | Date, timeZone: string): number {
  return new Date(new Date(value).toLocaleString('en-US', { timeZone })).getTime()
}

export function hasScheduledWebinarEnded(event: WebinarEvent, timeZone: string): boolean {
  if (event.status === 'ENDED') return true
  if (!event.scheduledEndAt) return false

  const scheduledEndAtTime = getTimeInZone(event.scheduledEndAt, timeZone)
  return (
    Number.isFinite(scheduledEndAtTime) &&
    scheduledEndAtTime <= getTimeInZone(new Date(), timeZone)
  )
}

export function buildOnlineSessionRows(
  date: string,
  visibleWebinarEvents: WebinarEvent[]
): OnlineSessionRow[] {
  if (date < ONLINE_SLOTS_START_DATE) {
    return []
  }

  const webinarByTime = new Map<string, WebinarEvent>()
  visibleWebinarEvents.forEach(event => {
    webinarByTime.set(getWebinarRawTime(event), event)
  })

  return [...webinarByTime.keys()].sort().flatMap(rawTime => {
    const event = webinarByTime.get(rawTime)
    if (!event) {
      return []
    }

    return [
      {
        rawTime,
        time: formatSlotTime(rawTime),
        event
      }
    ]
  })
}
